import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { LinhaDesempenho, ColunaDesempenho, NotaView, ItemEducacional, Periodo } from '../services/nota.service';
import { ConfiguracaoAvaliacao } from '../../../core/config/services/avaliacao.service';
import { DateUtils } from '../../../shared/date-utils';

interface Turma {
  id: number;
  separacaoAlunosEmGrupos: boolean;
  tipoResultado?: string;
}

@Component({
  selector: 'app-desempenho-instrumento',
  templateUrl: './desempenho-instrumento.component.html',
  styleUrls: ['./desempenho-instrumento.component.scss']
})
export class DesempenhoInstrumentoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() colunas: ColunaDesempenho[] = [];
  @Input() linhas: LinhaDesempenho[] = [];
  @Input() colunaMediaPeriodo: ColunaDesempenho | null = null;
  @Input() configuracaoAvaliacao: ConfiguracaoAvaliacao = {};
  @Input() configuracaoCha: Record<string, unknown> = {};
  @Input() hasPermissionsToEdit = false;
  @Input() calculoFormula: unknown = null;
  @Input() turma: Turma | null = null;
  @Input() itemEducacional: ItemEducacional | null = null;
  @Input() periodo: Periodo | null = null;
  @Input() enumSituacaoMatricula: Record<string, { description?: string; value?: unknown }> = {};
  @Input() enumSituacaoDisciplina: Record<string, { description?: string; value?: unknown }> = {};
  @Input() grupoEnturmacaoCor: Record<string, { description?: string; value?: unknown; hex?: string }> = {};
  @Input() isLoading = false;
  @Input() ocultarDetalhes = false;
  @Input() semCalendario = false;
  @Input() semPeriodoAvaliativo = false;
  @Input() filtroAtivo = false;

  @Output() salvar = new EventEmitter<NotaView>();
  @Output() showParecer = new EventEmitter<unknown>();
  @Output() showAvaliacaoDescritiva = new EventEmitter<unknown>();
  @Output() togglePublicacao = new EventEmitter<unknown>();

  currentItemIndex = 0;
  currentItemEducacional: ItemEducacional | null = null;

  private destroy$ = new Subject<void>();

  get hasColunas(): boolean {
    return this.colunas && this.colunas.length > 0;
  }

  get hasAvaliacoes(): boolean {
    return this.hasItensList && this.hasColunas;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['linhas'] || changes['colunas']) {
      this.isLoading = false;
    }
    if (changes['itemEducacional']) {
      this.updateCurrentItem();
    }
  }

  ngOnInit() {
    this.updateCurrentItem();
  }

  private updateCurrentItem() {
    this.currentItemEducacional = this.itemEducacional;
  }

  get hasItensList(): boolean {
    return this.linhas && this.linhas.length > 0;
  }

  getColunaDescricao(nota: NotaView): string {
    if (!nota || !nota.avaliavel) return '';

    if (nota.avaliavel.tipoPeriodo === 'CALCULO_INSTRUMENTOS') {
      return 'Cálculo';
    }

    return nota.avaliavel.sigla
      ? nota.avaliavel.sigla.toUpperCase() 
      : (nota.avaliavel.descricao || '');
  }

  formatDate(date: string | Date | undefined): string {
    return DateUtils.formatarDataCurta(date);
  }

  get hasAnyConfiguracaoCha(): boolean {
    return Object.values(this.configuracaoCha).some((config) => (config as { visivel?: boolean })?.visivel);
  }

  get canNavigatePrev(): boolean {
    return this.currentItemIndex > 0;
  }

  get canNavigateNext(): boolean {
    return this.currentItemIndex < (this.linhas.length - 1);
  }

  orderBy(item: LinhaDesempenho): number {
    return item.enturmacao.numeroChamada;
  }

  verificaDataFimEnturmacao(nota: NotaView): boolean {
    if (!nota.avaliavel?.data) return false;
    
    const enturmacaoCompleta = nota.enturmacao as { dataFim?: string } | undefined;
    if (!enturmacaoCompleta?.dataFim) return false;

    const dataItem = new Date(nota.avaliavel.data);
    const dataFim = new Date(enturmacaoCompleta.dataFim);

    return dataItem > dataFim;
  }

  /** Opt-out: exibe parecer sempre que PARECER.visivel não for explicitamente false */
  showParecerMethod(): boolean {
    return this.configuracaoAvaliacao.PARECER?.visivel !== false;
  }

  showMediaCha(nota: NotaView): boolean {
    return nota.avaliavel.tipoPeriodo !== 'EXAME_FINAL';
  }

  showModalChaObjetivo(nota: NotaView | undefined): boolean {
    if (!nota || !nota.avaliavel) return false;
    const tipoPeriodo = nota.avaliavel.tipoPeriodo;
    return tipoPeriodo === 'EXAME_FINAL' || tipoPeriodo === 'MEDIA_PERIODO';
  }

  getSituacao(linha: LinhaDesempenho): string | null {
    let situacaoEnturmacao = linha.enturmacao.situacao;
    const situacaoMatricula = linha.enturmacao.matricula?.situacao;

    const situacoesItens = (linha.enturmacao as any).situacaoItensEducacionais;
    if (this.itemEducacional && Array.isArray(situacoesItens)) {
      const sitEspecifica = situacoesItens.find((s: any) => s.itemEducacional?.id === this.itemEducacional?.id);
      if (sitEspecifica && sitEspecifica.situacao) {
        situacaoEnturmacao = sitEspecifica.situacao;
      }
    }
    
    // Fallback manual: cobre situações que às vezes não vêm nos enums
    const situacaoMap: { [key: string]: string } = {
      'CURSANDO': 'Cursando',
      'APROVADO': 'Aprovado',
      'REPROVADO': 'Reprovado',
      'RECUPERACAO': 'Recuperação',
      'EM_RECUPERACAO': 'Em Recuperação',
      'TRANSFERIDO': 'Transferido',
      'EVADIDO': 'Evadido'
    };
    
    let situacao = situacaoEnturmacao ? this.enumSituacaoDisciplina[situacaoEnturmacao]?.description : undefined;

    if (!situacao) {
      situacao = situacaoEnturmacao ? this.enumSituacaoMatricula[situacaoEnturmacao]?.description : undefined;
    }

    if (!situacao && situacaoMatricula) {
      situacao = this.enumSituacaoMatricula[situacaoMatricula]?.description;
    }

    if (!situacao && situacaoMatricula) {
      situacao = this.enumSituacaoDisciplina[situacaoMatricula]?.description;
    }

    if (!situacao && situacaoEnturmacao) {
      const situacaoUpper = situacaoEnturmacao.toUpperCase();
      const keyDisciplina = Object.keys(this.enumSituacaoDisciplina).find(
        k => k.toUpperCase() === situacaoUpper
      );
      if (keyDisciplina) {
        situacao = this.enumSituacaoDisciplina[keyDisciplina]?.description;
      }
    }
    
    if (!situacao && situacaoEnturmacao) {
      const situacaoUpper = situacaoEnturmacao.toUpperCase();
      const keyMatricula = Object.keys(this.enumSituacaoMatricula).find(
        k => k.toUpperCase() === situacaoUpper
      );
      if (keyMatricula) {
        situacao = this.enumSituacaoMatricula[keyMatricula]?.description;
      }
    }
    
    if (!situacao && situacaoEnturmacao) {
      situacao = situacaoMap[situacaoEnturmacao.toUpperCase()];
    }
    
    if (!situacao && situacaoMatricula) {
      situacao = situacaoMap[situacaoMatricula.toUpperCase()];
    }
    

    const mostraSituacao = this.turma?.tipoResultado !== 'AREA_CONHECIMENTO';
    return mostraSituacao ? (situacao || null) : null;
  }

  getSituacaoMatriculaBadge(linha: LinhaDesempenho): string {
    const situacoesItens = (linha.enturmacao as any).situacaoItensEducacionais;
    if (this.itemEducacional && Array.isArray(situacoesItens)) {
      const sitEspecifica = situacoesItens.find((s: any) => s.itemEducacional?.id === this.itemEducacional?.id);
      if (sitEspecifica && sitEspecifica.situacao) {
        return sitEspecifica.situacao;
      }
    }

    return linha.enturmacao.situacao || linha.enturmacao.matricula?.situacao || '';
  }

  getGrupoCor(grupo: string): string {
    return this.grupoEnturmacaoCor[grupo]?.hex || '#6c757d';
  }

  navigatePrev() {
    if (this.canNavigatePrev) {
      this.currentItemIndex--;
      this.updateCurrentItem();
    }
  }

  navigateNext() {
    if (this.canNavigateNext) {
      this.currentItemIndex++;
      this.updateCurrentItem();
    }
  }

  onSalvar(nota: NotaView): void {
    this.salvar.emit(nota);
  }

  onShowParecer(nota: NotaView | undefined): void {
    if (!nota) return;
    this.showParecer.emit(nota);
  }

  onShowAvaliacaoDescritiva(nota: NotaView | undefined): void {
    if (!nota) return;
    this.showAvaliacaoDescritiva.emit(nota);
  }

  onTogglePublicacao(nota: NotaView | undefined): void {
    if (!nota) return;
    this.togglePublicacao.emit(nota);
  }

  onShowCHA(nota: NotaView | undefined): void {
    if (!nota) return;
    // Implementar modal CHA
  }

  onShowObjetivos(nota: NotaView | undefined): void {
    if (!nota) return;
    // Implementar modal objetivos
  }

  getErroDaLinha(linha: LinhaDesempenho): string | null {
    if (!linha) return null;

    if (linha.notaMediaPeriodo?.$$mensagemValidacao) {
      return linha.notaMediaPeriodo.$$mensagemValidacao;
    }

    if (linha.notas && linha.notas.length > 0) {
      const notaComErro = linha.notas.find(n => n && n.$$mensagemValidacao);
      if (notaComErro) {
        return notaComErro.$$mensagemValidacao || null;
      }
    }

    return null;
  }

  trackByEnturmacao(index: number, linha: LinhaDesempenho): number {
    return linha.enturmacao.id;
  }

  trackByColuna(index: number, coluna: ColunaDesempenho): string {
    return coluna.rawValue?.id?.toString() || index.toString();
  }

  trackByNota(index: number, nota: NotaView): string {
    return nota.$$nocache || index.toString();
  }

  onFotoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = '/images/default-user.png';
    }
  }

}